import {Callout} from '@wandb/weave/components/Callout';
import {Icon, IconName} from '@wandb/weave/components/Icon';
import {TagColorName} from '@wandb/weave/components/Tag';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {useRampFlagSemanticLegend} from '../../../util/rampFeatureFlags';
import {CloseButton} from './components/CloseButton';
import {
  WORKSPACE_LINE_PLOT_SETTINGS_DESCRIPTION,
  WORKSPACE_SEMANTIC_LEGENDS_SETTINGS_DESCRIPTION,
} from './constants';
import {SelectedSettingType} from './types';

export const SettingsMenu = ({
  entityName,
  setSelectedSettingType,
}: {
  entityName: string;
  setSelectedSettingType: (type: SelectedSettingType | undefined) => void;
}) => {
  return (
    <div className="flex flex-col" data-test="workspace-settings-menu">
      <div
        className={twMerge(
          'flex items-center justify-between',
          'px-20 py-16',
          'border-b-[1px] border-solid border-moon-250'
        )}>
        <h1 className={twMerge('text-xl font-semibold leading-[32px]')}>
          Workspace settings
        </h1>
        <CloseButton />
      </div>
      <SettingsMenuItem
        dataTest="workspace-layout-menu-item"
        icon="layout-grid"
        color="cactus"
        title="Workspace layout"
        description="Define the overall structure of this workspace, including sectioning logic and panel organization."
        onClick={() => setSelectedSettingType('workspace-layout')}
      />
      {useRampFlagSemanticLegend(entityName) && (
        <SettingsMenuItem
          dataTest="workspace-semantic-legend-menu-item"
          icon="running-repeat"
          color="green"
          title="Runs"
          description={WORKSPACE_SEMANTIC_LEGENDS_SETTINGS_DESCRIPTION}
          onClick={() => setSelectedSettingType('semantic-legend')}
        />
      )}
      <SettingsMenuItem
        dataTest="workspace-line-plot-menu-item"
        icon="line-plot-alt2"
        color="blue"
        title="Line plots"
        description={WORKSPACE_LINE_PLOT_SETTINGS_DESCRIPTION}
        onClick={() => setSelectedSettingType('line-plot')}
      />
    </div>
  );
};

const SettingsMenuItem = ({
  dataTest,
  icon,
  color,
  title,
  description,
  onClick,
}: {
  dataTest: string;
  icon: IconName;
  color: TagColorName;
  title: string;
  description: string;
  onClick: () => void;
}) => {
  return (
    <button
      data-test={dataTest}
      type="button"
      onClick={onClick}
      className={twMerge(
        'gap-20 py-16 pl-24 pr-20',
        'flex items-center ',
        'hover:cursor-pointer hover:bg-moon-100',
        'text-left',
        'min-h-[6rem]',
        'border-b-[1px] border-solid border-moon-250'
      )}>
      <Callout color={color} icon={icon} size="x-small" />
      <div className="flex w-full flex-col">
        <h2 className={twMerge('text-base font-semibold leading-[24px]')}>
          {title}
        </h2>
        <p className={twMerge('text-base leading-[140%] text-moon-500')}>
          {description}
        </p>
      </div>
      <Icon
        name="chevron-next"
        className={twMerge('text-moon-500', 'shrink-0 p-0')}
      />
    </button>
  );
};
